// Copyright 2016 Sorama B.V.

import { CompositeUnit } from "units/CompositeUnit";
import { Currency } from "units/Currency";
import { Energy } from "units/Energy";
import { Length } from "units/Length";
import { Mass } from "units/Mass";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue } from "units/Unit";
import { UnitSystem } from "units/UnitSystem";
import { Volume } from "units/Volume";
import { FlowEffort, VolumeFlow } from "units/VolumeFlow";
import { As2AssetStatus, As2AssetTypes, As2DischargeType, As2Frequency, As2LeakType } from "./FlukeAs2Implementation";
import { ReportableMeasurement } from "./ReportableMeasurement";
import { PDDDischarge } from "./SoramaAs2Implementation";
import { GenericMeasurement, SpecialMeasurementType } from "./SoramaWebApi";

export type AnyReportType = RTData | SRIData | SLRData | LDData | PDDData | MixedData;

export interface SRIData {
    clientName: string;
    companyName: string;
    investigatedObject: string;
    isFieldMeasurement: boolean;
    lrDimension1?: number;
    lrDimension2?: number;
    lrDimension3?: number;
    roomVolume?: number;
    measurementLocation: string;
    reverberationTime: number;
    soundReductionIndex: [];
    swDimension1?: number;
    swDimension2?: number;
    roomSurfaceArea?: number;
    weightedSoundReductionIndex: number;
    shiftedReference?: number[];
    isSTC?: boolean;
}

export interface RTData {
    client: string;
    companyName: string;
    decayTime: RTDecayTime;
    isFieldMeasurement: boolean;
    measurementDevice: string;
    measurementLocation: string;

    octaveMode: number;
    precision: number;

    rt20ReverberationTimePerOctaveBand: number[];
    rt30ReverberationTimePerOctaveBand: number[];
    rt60ReverberationTimePerOctaveBand: number[];

    reverberationTime: number;

    signal: string;
    volumeReceivingRoom: string;

    // Compatible with previous version
    measuredTime?: string;
}
export enum RTDecayTime {
    RT20 = 0,
    RT30 = 1,
    Undefined = 2,
}

export interface SLRData {
    investigatedObject: string;
    productName: string;
    operatingConditions: string;
    operatorName: string;
    manufacturerName: string;
    testLabName: string;
    testDate: string;
    reverberationTimeLoud: number;
    reverberationTimeReduced: number;
    roomSurfaceArea: number;
    roomVolume: number;
    speechLevelReduction: number[];
    weightedSpeechLevelReduction: number;
}

export interface Discharge {
    external: number;
    internal: number;
    tracking: number;
    other: number;
}

export interface PDDData {
    operatingFrequency: As2Frequency;
    pulseCount: number;
    dischargeTypesPercentages: Discharge;
    weatherNotes: string;
    notes: string;
    dischargeType: As2DischargeType;

    pictures: unknown[];

    assetName?: string;
    assetType?: As2AssetTypes;
    actionRequired?: string;
    ambientTemperature?: string;
    actionNotes?: string;
    actionPriority?: string;
    assetCalibrationStatus?: As2AssetStatus;
    assetId?: string;
    location?: string;
    relativeHumidity?: string;
    voltage?: string;

    // These values are retrieved from the API separately from the rest or the data, and manually added to the object before rendering a report
    reportDischarges: PDDDischarge[];
}

export interface LDData {
    distance: UnitValue;
    measuredDBLevel: UnitValue;
    leakType: As2LeakType;
    automaticLeakType: As2LeakType;

    estimatedLeakRate?: UnitValue;
    estimatedKwh?: UnitValue;
    estimatedCosts?: UnitValue;
    estimatedCO2Emission?: UnitValue;
    notes: string;

    pictures: unknown[];

    assetId?: string;
    assetName?: string;
    assetType?: string;
    inspectionStatus?: string;
    actionNotes?: string;
    actionPriority?: string;
    actionRequired?: string;
    location?: string;

    // Operating conditions. Needs to be separated more? operatingConditions: OperatingConditions;
    unitSystem: UnitSystem;
    currency: Unit;
    gasType: string;
    gasCost: UnitValue;
    electricityCost: UnitValue;
    powerToFlowRatio: UnitValue;
    systemPressure: UnitValue;
    operatingHours: number;
    co2EmissionFactorInputMethod: "By location" | "Manual";
    country: string;
    stateRegion: string;
    manualCO2Factor?: UnitValue;
}

export interface MixedData {
    distance: UnitValue;
    measuredDBLevel: UnitValue;
    leakType: As2LeakType;
    automaticLeakType: As2LeakType;
    estimatedLeakRate?: UnitValue;
    estimatedKwh?: UnitValue;
    estimatedCosts?: UnitValue;
    estimatedCO2Emission?: UnitValue;
    gasType: string;
    gasCost: UnitValue;
    electricityCost: UnitValue;
    powerToFlowRatio: UnitValue;
    systemPressure: UnitValue;
    operatingHours: number;
    co2EmissionFactorInputMethod: "By location" | "Manual";
    country: string;
    stateRegion: string;
    manualCO2Factor?: UnitValue;
    operatingFrequency: As2Frequency;
    pulseCount: number;
    dischargeTypesPercentages: Discharge;
    weatherNotes: string;
    notes: string;
    dischargeType: As2DischargeType;
    pictures: unknown[];
    assetName?: string;
    assetType?: As2AssetTypes;
    actionRequired?: string;
    ambientTemperature?: string;
    actionNotes?: string;
    actionPriority?: string;
    assetCalibrationStatus?: As2AssetStatus;
    assetId?: string;
    location?: string;
    relativeHumidity?: string;
    voltage?: string;
}

export function genericMeasurementToReportableMeasurement(m: GenericMeasurement): ReportableMeasurement<AnyReportType> {
    const report = {
        id: m.id,
        name: m.specialMeasurementInfo?.metadata?.survey?.measurementName ?? m.name,
        surveyName: m.specialMeasurementInfo?.metadata?.survey?.surveyName ?? "",
        company: "",
        created: new Date(m.start),
        notes: m.notes,
        type: m.specialMeasurementType,
    };
    const data = m.specialMeasurementInfo;

    const defaultMixed: MixedData = {
        distance: new UnitValue(0, Length.meter),
        measuredDBLevel: new UnitValue(0, Pressure.pascal),
        leakType: As2LeakType.ThreadedCap,
        automaticLeakType: As2LeakType.ThreadedCap,
        estimatedLeakRate: new UnitValue(0, Length.meter),
        estimatedKwh: new UnitValue(0, Energy.kiloWattHours),
        estimatedCosts: new UnitValue(0, Currency.EUR),
        estimatedCO2Emission: new UnitValue(0, Mass.kiloGram),
        gasType: "Air",
        gasCost: new UnitValue(0, Currency.EUR),
        electricityCost: new UnitValue(0.2, Currency.EUR),
        powerToFlowRatio: new UnitValue(1.05, FlowEffort.kwPer100LiterMinute),
        systemPressure: new UnitValue(5, Pressure.bar),
        operatingHours: 8760,
        co2EmissionFactorInputMethod: "By location",
        country: "Netherlands",
        stateRegion: "Netherlands Average",
        manualCO2Factor: new UnitValue(0.5, Mass.kiloGram),
        operatingFrequency: As2Frequency[1],
        pulseCount: 29999,
        dischargeTypesPercentages: { external: 0, internal: 0, tracking: 0, other: 0 },
        weatherNotes: "",
        notes: "",
        dischargeType: As2DischargeType[1],
        pictures: undefined,
        assetName: "",
        assetType: As2AssetTypes[0],
        actionRequired: "",
        ambientTemperature: "",
        actionNotes: "",
        actionPriority: "",
        assetCalibrationStatus: As2AssetStatus[0],
        assetId: "",
        location: "",
        relativeHumidity: "",
        voltage: "",
    };

    if (!data) throw new Error("No special measurement data found");

    if (report.type === SpecialMeasurementType.RT) {
        const rtData: RTData = {
            client: data.Client,
            companyName: data.CompanyName,
            decayTime: data.DecayTime,
            isFieldMeasurement: data.IsFieldMeasurement,
            measurementDevice: data.MeasurementDevice,
            measurementLocation: data.MeasurementLocation,
            octaveMode: data.OctaveMode,
            precision: data.Precision,
            rt20ReverberationTimePerOctaveBand: data.RT20ReverberationTimePerOctaveBand,
            rt30ReverberationTimePerOctaveBand: data.RT30ReverberationTimePerOctaveBand,
            rt60ReverberationTimePerOctaveBand: data.RT60ReverberationTimePerOctaveBand,
            reverberationTime: data.ReverberationTime,
            signal: data.Signal,
            volumeReceivingRoom: data.VolumeReceivingRoom,
            measuredTime: data.MeasuredTime,
        };
        return {
            ...report,
            data: rtData,
        } as ReportableMeasurement<RTData>;
    }
    if (report.type === SpecialMeasurementType.SRI) {
        return {
            ...report,
            data: data,
        } as ReportableMeasurement<SRIData>;
    }

    if (report.type === SpecialMeasurementType.STC) {
        data.isSTC = true;
        return {
            ...report,
            data: data,
        } as ReportableMeasurement<SRIData>;
    }

    if (report.type === SpecialMeasurementType.SLR) {
        return {
            ...report,
            data: data,
        } as ReportableMeasurement<SLRData>;
    }

    if (report.type === SpecialMeasurementType.LD) {
        const ldData: LDData = {
            estimatedLeakRate: new UnitValue(data.automaticLeakRate, VolumeFlow.litersPerMinute),
            distance: new UnitValue(0, Length.meter),
            measuredDBLevel: new UnitValue(Number(data.metadata.leakQ.soundPressureLevelAtSource), Pressure.dbASPL),
            leakType: data.automaticLeakType,
            automaticLeakType: data.automaticLeakType,
            inspectionStatus: undefined,
            notes: data.textNote,
            pictures: data.pictures,

            // operating conditions
            unitSystem: UnitSystem.Metric,
            currency: Currency.EUR,
            gasType: "",
            systemPressure: new UnitValue(0, Pressure.bar),
            gasCost: new UnitValue(0, Currency.EUR),
            electricityCost: new UnitValue(0, Currency.EUR),
            powerToFlowRatio: new UnitValue(0, FlowEffort.kwPerCfm),
            operatingHours: 8760,
            co2EmissionFactorInputMethod: "Manual",
            manualCO2Factor: new UnitValue(0.5, Mass.kiloGram),
            country: "Netherlands",
            stateRegion: "Netherlands Average",
        };

        const leakq = data.metadata.leakQ;
        if (leakq) {
            if (leakq.distanceToSource && leakq.distanceUnit)
                ldData.distance = new UnitValue(leakq.distanceToSource, Length[leakq.distanceUnit.toLowerCase()]);
            if (leakq.manualDistance && leakq.distanceUnit)
                ldData.distance = new UnitValue(leakq.manualDistance, Length[leakq.distanceUnit.toLowerCase()]);
        }

        const tags = data.metadata.tags;
        if (tags) {
            if (tags.pressure && tags.pressureUnit)
                ldData.systemPressure = new UnitValue(
                    data.metadata.tags.pressure,
                    Pressure[tags.pressureUnit.toLowerCase()]
                );
            if (tags.actionNotes) ldData.actionNotes = tags.actionNotes;
            if (tags.actionPriority) ldData.actionPriority = tags.actionPriority;
            if (tags.actionRequired) ldData.actionRequired = tags.actionRequired;
            if (tags.assetCalibrationStatus) ldData.inspectionStatus = tags.assetCalibrationStatus;
            if (tags.assetId) ldData.assetId = tags.assetId;
            if (tags.assetName) ldData.assetName = tags.assetName;
            if (tags.assetType) ldData.assetType = tags.assetType;
            if (tags.gasType) ldData.gasType = tags.gasType;
            if (tags.location) ldData.location = tags.location;
            if (tags.leakType && As2LeakType[tags.leakType]) ldData.leakType = tags.leakType;
            if (tags.manualLeakType && As2LeakType[tags.manualLeakType]) ldData.leakType = tags.manualLeakType;
        }

        const frontEndSettings = data.metadata?.instrument?.settings?.Frontend;
        if (frontEndSettings) {
            if (frontEndSettings.unitSystem) ldData.unitSystem = UnitSystem[frontEndSettings.unitSystem];
        }

        //Currently the operating conditions overwrite tags. Not sure if thats the end goal, but need to check device for that.
        const operatingConditions = data.metadata?.profile?.settings?.operatingConditions;
        if (operatingConditions?.length >= 1) {
            const currencyString = operatingConditions[0].currency;
            ldData.currency = currencyString ? Currency[currencyString] : Currency.EUR;

            if (operatingConditions[0].operatingConditionsDecimals) {
                const decimals = operatingConditions[0].operatingConditionsDecimals;
                const toFixedNumber = (num, digits) => {
                    const pow = Math.pow(10, digits);
                    return Math.round(num * pow) / pow;
                };

                if (operatingConditions[0].costsOfGas) {
                    // AS2 files contain gast cost in selected currency, but always per 1000L (so independent of selected unit system)
                    const gasCostPer1000L = operatingConditions[0].costsOfGas;
                    // Conversion factor from unit system of gas cost in file (per 1000L) to selected unit system (per SCF or per 1000L)
                    const volumeCostUnit = ldData.unitSystem.volumeCost as CompositeUnit;
                    const conversionFactor =
                        new UnitValue(1, volumeCostUnit.denominator).value /
                        new UnitValue(1000, Volume.liters).toUnit(volumeCostUnit.denominator);

                    ldData.gasCost = new UnitValue(
                        toFixedNumber(conversionFactor * gasCostPer1000L, decimals),
                        ldData.currency
                    );
                }

                if (operatingConditions[0].costsOfElectricity)
                    ldData.electricityCost = new UnitValue(
                        toFixedNumber(operatingConditions[0].costsOfElectricity, decimals),
                        ldData.currency
                    );

                if (operatingConditions[0].ratioOfPowerToFlowRate) {
                    // Power to flow ratio is always defined as kw/100L/min in AS2 files, independent of selected unit system
                    // (User input in imperial unit system is in kW/100CFM, but this is converted when writing to file)
                    const powerToFlowPer100LiterPerMinute = new UnitValue(
                        operatingConditions[0].ratioOfPowerToFlowRate,
                        FlowEffort.kwPer100LiterMinute
                    );
                    const powerToFlowInUnitSystem = powerToFlowPer100LiterPerMinute.toUnit(
                        ldData.unitSystem.flowEffort
                    );

                    ldData.powerToFlowRatio = new UnitValue(
                        toFixedNumber(powerToFlowInUnitSystem, decimals),
                        ldData.unitSystem.flowEffort
                    );
                }

                const pressureUnitKey = operatingConditions[0].operatingPressureUnit;
                const pressureUnit = pressureUnitKey
                    ? Pressure[Object.keys(Pressure).find((key) => key.toLowerCase() === pressureUnitKey.toLowerCase())]
                    : Pressure.bar;

                if (operatingConditions[0].operatingPressure)
                    ldData.systemPressure = new UnitValue(
                        toFixedNumber(operatingConditions[0].operatingPressure, decimals),
                        pressureUnit
                    );
            }

            if (operatingConditions[0].operatingHoursPerYear)
                ldData.operatingHours = data.metadata.profile.settings.operatingConditions[0]?.operatingHoursPerYear;
        }
        return {
            ...report,
            data: Object.assign(defaultMixed, ldData),
        } as ReportableMeasurement<LDData>;
    }
    if (report.type === SpecialMeasurementType.PDD) {
        const pddData: PDDData = {
            operatingFrequency: undefined,
            pulseCount: data.metadata?.pdq?.dischargePulsesPerMinute,
            dischargeType: undefined,
            dischargeTypesPercentages: {
                external: data.externalAmount ?? data.ExternalAmount,
                internal: data.internalAmount ?? data.InternalAmount, // Pull backend and reupload your AS2s to have them available in camelCase
                tracking: data.trackingAmount ?? data.TrackingAmount,
                other: data.otherAmount ?? data.OtherAmount,
            },
            weatherNotes: "",
            notes: data.textNote,
            pictures: data.pictures,
            reportDischarges: undefined,
        };

        const tags = data.metadata?.tags;
        if (tags) {
            if (tags.actionNotes) pddData.actionNotes = tags.actionNotes;
            if (tags.actionPriority) pddData.actionPriority = tags.actionPriority;
            if (tags.actionRequired) pddData.actionRequired = tags.actionRequired;
            if (tags.ambientTemperature) pddData.ambientTemperature = tags.ambientTemperature;
            if (tags.assetCalibrationStatus) pddData.assetCalibrationStatus = tags.assetCalibrationStatus;
            if (tags.assetId) pddData.assetId = tags.assetId;
            if (tags.assetName) pddData.assetName = tags.assetName;
            if (tags.assetType) pddData.assetType = tags.assetType;
            if (tags.dischargeType) pddData.dischargeType = tags.dischargeType;
            if (tags.frequency) pddData.operatingFrequency = tags.frequency;
            if (tags.location) pddData.location = tags.location;
            if (tags.relativeHumidity) pddData.relativeHumidity = tags.relativeHumidity;
            if (tags.voltage) pddData.voltage = tags.voltage;
            if (tags.weatherNote) pddData.weatherNotes = tags.weatherNote;
        }

        const operatingConditions = data.metadata?.profile?.settings?.operatingConditions;
        if (operatingConditions?.length >= 1) {
            // The field 'operatingFrequencyPDQ' contains an integer number which corresponds with the _index_ of the enum value in As2Frequency, not the string value.
            const operatingFrequencyEnumIndex = operatingConditions[0].operatingFrequencyPDQ;

            if (operatingFrequencyEnumIndex < Object.keys(As2Frequency).length) {
                pddData.operatingFrequency = As2Frequency[Object.keys(As2Frequency)[operatingFrequencyEnumIndex]];
            }
        }

        return {
            ...report,
            data: Object.assign(defaultMixed, pddData),
        } as ReportableMeasurement<PDDData>;
    }

    throw new Error("Unknown special measurement type");
}
